import { useState } from "react";
import { Lock, LockOpen, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCan } from "@/lib/auth/useCan";

import {
  useLockSnapshot,
  useSnapshot,
  useUnlockSnapshot,
} from "../api";
import { isoWeekEnd } from "../format";

/**
 * Three-state lock button for the current FilterBar week:
 *  - "Lock available after Wk ends" — disabled, today ≤ Sunday of Wk.
 *  - "Lock week N" — enabled, Stan can capture + freeze the snapshot.
 *  - "Locked ✓" — already locked; admins see an Unlock affordance.
 *
 * Locking writes ``is_locked=true`` on the most recent snapshot for the
 * week (or captures a fresh one first if the cron missed it). Once locked,
 * the cron + manual capture paths refuse to overwrite the payload, so the
 * sparklines + historical comparisons read a frozen point-in-time view.
 */
export function WeekLockButton({
  year,
  week,
}: {
  year: number;
  week: number;
}) {
  const snapshot = useSnapshot(year, week);
  const lockMutation = useLockSnapshot();
  const unlockMutation = useUnlockSnapshot();
  const canUnlock = useCan("cro_forecast.admin");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sunday = isoWeekEnd(year, week);
  const weekEnded = today > sunday;

  const isLocked = snapshot.data?.is_locked === true;
  const isMonthFinal = snapshot.data?.is_month_final === true;
  // The cron captures (or Stan can manually capture) a snapshot for the
  // week without locking it. When that snapshot exists but isn't locked,
  // the per-tab views show its data instead of live queries, but edits
  // are still allowed — "frozen". The Lock button surfaces this state
  // so Stan knows what the click is committing to.
  const hasUnlockedSnapshot = snapshot.data != null && !isLocked;

  function confirmLock() {
    setConfirmOpen(false);
    lockMutation.mutate(
      { year, week },
      {
        onSuccess: () => toast.success(`Wk ${week} locked.`),
        onError: (err) => {
          const detail =
            (err as { payload?: { detail?: unknown } }).payload?.detail ??
            (err as Error).message;
          toast.error(
            typeof detail === "string" ? detail : `Lock failed: ${(err as Error).message}`,
          );
        },
      },
    );
  }

  function handleUnlock() {
    unlockMutation.mutate(
      { year, week },
      {
        onSuccess: () => toast.success(`Wk ${week} unlocked.`),
        onError: (err) => {
          const detail =
            (err as { payload?: { detail?: unknown } }).payload?.detail ??
            (err as Error).message;
          toast.error(
            typeof detail === "string" ? detail : `Unlock failed: ${(err as Error).message}`,
          );
        },
      },
    );
  }

  if (snapshot.isLoading) {
    return (
      <Button size="sm" variant="outline" disabled>
        <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        Loading…
      </Button>
    );
  }

  // STATE 1: locked.
  if (isLocked) {
    return (
      <TooltipProvider delayDuration={250}>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1 rounded-md border border-success/40 bg-success/10 px-2 py-1 text-xs font-semibold text-success">
                <Lock className="h-3.5 w-3.5" />
                {isMonthFinal ? "Final" : `Locked Wk ${week}`}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs text-xs">
              Snapshot captured at{" "}
              {snapshot.data?.captured_at
                ? new Date(snapshot.data.captured_at).toLocaleString()
                : "—"}
              {isMonthFinal && (
                <>
                  <br />
                  Month-final snapshots stay permanently locked.
                </>
              )}
            </TooltipContent>
          </Tooltip>
          {canUnlock && !isMonthFinal && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleUnlock}
              disabled={unlockMutation.isPending}
              title="Unlock (admin)"
            >
              <LockOpen className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </TooltipProvider>
    );
  }

  // STATE 2: week not yet ended.
  if (!weekEnded) {
    return (
      <TooltipProvider delayDuration={250}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button size="sm" variant="outline" disabled>
                <Lock className="mr-1.5 h-3.5 w-3.5" />
                Lock after Wk ends
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs text-xs">
            Locking is only available once the week's Sunday has passed —
            otherwise the snapshot would freeze mid-week.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // STATE 3: week ended, not yet locked. The cron-captured snapshot
  // (if present) makes this a "frozen → confirm to lock" affordance.
  return (
    <>
      <Button
        size="sm"
        variant={hasUnlockedSnapshot ? "default" : "outline"}
        onClick={() => setConfirmOpen(true)}
        disabled={lockMutation.isPending}
        title={
          hasUnlockedSnapshot
            ? "Snapshot captured — click to lock the week against further edits"
            : `Lock Wk ${week}`
        }
      >
        <Lock className="mr-1.5 h-3.5 w-3.5" />
        {lockMutation.isPending ? "Locking…" : `Lock Wk ${week}`}
      </Button>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lock Wk {week} {year}?</DialogTitle>
            <DialogDescription className="space-y-2 pt-1">
              <span className="block">
                Locking commits this week's snapshot as the historical
                record. After locking:
              </span>
              <ul className="list-disc pl-5 text-sm">
                <li>
                  All notes, weekly calls, AE submissions, and overrides
                  for this week will be <strong>read-only</strong>.
                </li>
                <li>
                  The locked payload becomes what every tab shows for
                  Wk {week} going forward.
                </li>
                <li>
                  Only an admin can unlock — month-final snapshots are
                  permanent.
                </li>
              </ul>
              {hasUnlockedSnapshot && (
                <span className="block pt-1 text-xs text-muted-foreground">
                  Snapshot captured at{" "}
                  {snapshot.data?.captured_at
                    ? new Date(snapshot.data.captured_at).toLocaleString()
                    : "—"}
                  .
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={lockMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmLock}
              disabled={lockMutation.isPending}
            >
              <Lock className="mr-1.5 h-3.5 w-3.5" />
              {lockMutation.isPending ? "Locking…" : "Lock week"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
